// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import {
  type CostDataUploadResult,
  uploadCostData,
  uploadCostDataResponseSchema,
} from '@/costs/api/upload-cost-data';
import { CsvUpload } from '@/costs/components/cost-data-upload/csv-upload';
import { Results } from '@/costs/components/cost-data-upload/results';
import { Stepper } from '@/costs/components/cost-data-upload/stepper';
import { TemplateDownload } from '@/costs/components/cost-data-upload/template-download';
import type {
  ExpandedCostsState,
  Step,
} from '@/costs/components/cost-data-upload/types';
import { useSupportError } from '@/hooks/use-support-error';
import { Box, CardContent } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useRef, useState } from 'react';

interface CostDataUploadProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export function CostDataUpload(props: CostDataUploadProps) {
  const { onClose, onSuccess } = props;
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<CostDataUploadResult | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [expandedCosts, setExpandedCosts] = useState<ExpandedCostsState>({
    created: false,
    failed: false,
    structureErrors: false,
  });
  const [hasFatalError, setHasFatalError] = useState(false);

  const clearFile = () => {
    setFile(null);
    setUploadLineCount(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    onClose();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('data', file);

      const result = await uploadCostData(formData);
      clearFile();
      setUploadResult(result);
      setCurrentStep('results');
      if (result.success && onSuccess) {
        onSuccess();
      }
    } catch (error) {
      clearFile();
      const axiosError = error as AxiosError;

      // First try to parse as a response with validation errors
      const knownError = uploadCostDataResponseSchema.safeParse(
        axiosError.response?.data
      );
      if (knownError.success) {
        setUploadResult(knownError.data.data);
        setUploadErrorMessage(null);
        setCurrentStep('results');
        // Only call onSuccess if the upload was actually successful (no errors)
        if (knownError.data.data.success && onSuccess) {
          onSuccess();
        }
        return;
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(axiosError);

      // Curated error code from ERROR_MESSAGES / VIOLATION_ERROR_MESSAGES:
      // show inline in dialog. Everything else (unmapped 4xx, 5xx, network)
      // rolls up to the support snackbar so backend text never leaks.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }

      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while uploading cost data.');
    } finally {
      setIsUploading(false);
    }
  }, [file, onSuccess, showSupportError, uploadLineCount]);

  const handleToggleExpanded = useCallback(
    (section: keyof ExpandedCostsState) => {
      setExpandedCosts((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    setFile(null);
    setUploadResult(null);
    setUploadErrorMessage(null);
    setUploadLineCount(undefined);
    setExpandedCosts({
      created: false,
      failed: false,
      structureErrors: false,
    });
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  return (
    <Box>
      <CardContent>
        <Stepper currentStep={currentStep} />

        {currentStep === 'download-template' && (
          <TemplateDownload onBack={handleClose} onNext={handleNext} />
        )}

        {currentStep === 'upload-csv' && (
          <CsvUpload
            uploadErrorMessage={uploadErrorMessage}
            file={file}
            isUploading={isUploading}
            onSelectFile={handleSelectFile}
            onUpload={handleUpload}
            onBack={handleBack}
            onClose={handleClose}
            hasFatalError={hasFatalError}
            uploadLineCount={uploadLineCount}
          />
        )}

        {currentStep === 'results' && (
          <Results
            uploadResult={uploadResult}
            expandedCosts={expandedCosts}
            onToggleExpanded={handleToggleExpanded}
            onBack={handleBack}
            onStartNew={handleStartNew}
            onClose={handleClose}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </CardContent>
    </Box>
  );
}
